import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { Input } from "./Input";

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.spacing(5)};
  min-height: ${({ theme }) => theme.spacing(5)};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.icon.muted};
  cursor: pointer;
  border-radius: ${({ theme }) => `${theme.radius.sm}px`};
  font-size: ${({ theme }) => theme.spacing(2.25)};
  transition: color ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${({ theme }) => theme.colors.accent.primary};
      background: ${({ theme }) => theme.colors.state.hover};
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.25)}
      ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Password input with a built-in show/hide toggle. Forwards every Input prop
 * (label, value, onChange, errorText, autoComplete, …) and manages only the
 * masked/revealed state. The toggle carries a bilingual aria-label.
 */
export const PasswordField = ({ ...props }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <Input
      {...props}
      type={visible ? "text" : "password"}
      endAdornment={
        <ToggleBtn
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-pressed={visible}
          aria-label={
            visible
              ? t("login.hide_password", { defaultValue: "Hide password" })
              : t("login.show_password", { defaultValue: "Show password" })
          }
          tabIndex={0}
        >
          {visible ? (
            <FiEyeOff aria-hidden="true" />
          ) : (
            <FiEye aria-hidden="true" />
          )}
        </ToggleBtn>
      }
    />
  );
};

export default PasswordField;
